package com.havn.app.core.billing

import com.havn.app.features.agency.domain.HavnSubscriptionTier

enum class HavnBillingPeriod {
    Monthly,
    Yearly
}

data class HavnStorePlanOption(
    val id: String,
    val tier: HavnSubscriptionTier,
    val period: HavnBillingPeriod,
    val price: String,
    val monthlyEquivalentPrice: String? = null,
    val productId: String
)

data class HavnStorePlans(
    val options: List<HavnStorePlanOption> = emptyList()
) {
    fun option(tier: HavnSubscriptionTier, period: HavnBillingPeriod): HavnStorePlanOption? =
        options.firstOrNull { it.tier == tier && it.period == period }
}

data class HavnBillingCustomerState(
    val activeEntitlementIds: Set<String> = emptySet(),
    val activeSubscriptionProductIds: Set<String> = emptySet(),
    val tier: HavnSubscriptionTier = HavnSubscriptionTier.Free
) {
    val hasActiveHavnSubscription: Boolean
        get() = tier != HavnSubscriptionTier.Free

    companion object {
        fun fromActiveEntitlements(activeEntitlementIds: Set<String>): HavnBillingCustomerState {
            return fromRevenueCatState(
                activeEntitlementIds = activeEntitlementIds,
                activeSubscriptionProductIds = emptySet()
            )
        }

        fun fromRevenueCatState(
            activeEntitlementIds: Set<String>,
            activeSubscriptionProductIds: Set<String>
        ): HavnBillingCustomerState {
            val entitlementTier = tierFromIdentifiers(activeEntitlementIds)
            val productTier = tierFromIdentifiers(activeSubscriptionProductIds)
            val tier = if (entitlementTier != HavnSubscriptionTier.Free) entitlementTier else productTier
            return HavnBillingCustomerState(
                activeEntitlementIds = activeEntitlementIds,
                activeSubscriptionProductIds = activeSubscriptionProductIds,
                tier = tier
            )
        }

        private fun tierFromIdentifiers(identifiers: Set<String>): HavnSubscriptionTier {
            val normalized = identifiers.map { it.lowercase() }.toSet()
            return when {
                normalized.any { it == "team_agent" || it.contains("team") } -> HavnSubscriptionTier.TeamAgent
                normalized.any { it == "pro_agent" || it.contains("pro") } -> HavnSubscriptionTier.ProAgent
                normalized.any { it == "solo_agent" || it.contains("solo") } -> HavnSubscriptionTier.SoloAgent
                else -> HavnSubscriptionTier.Free
            }
        }
    }
}

interface HavnBillingService {
    suspend fun loadPlans(appUserId: String): Result<HavnStorePlans>
    suspend fun purchase(option: HavnStorePlanOption, appUserId: String): Result<HavnBillingCustomerState>
    suspend fun restorePurchases(appUserId: String): Result<HavnBillingCustomerState>
}

class HavnPurchasePendingException : IllegalStateException("Purchase verification is pending.")

expect fun createHavnBillingService(): HavnBillingService
